import { fetchAuthSession } from "aws-amplify/auth";
import { useState } from "react";
import { toast } from "sonner";
import { RegulifyService } from "../api/RegulifyService";
import { useLoading } from "../context/LoadingContext";
import { buildWsUrl } from "../utils/wsUrl";
import userDoc from "../documents/DD_SDP_001 - Sample Device Software Rev.01.pdf";
import guidelineDoc from "../documents/Guideline Software Development Plan.pdf";
import ContentTitle from "../layout/ContentTitle";

const WS_PING_INTERVAL_MS = 20000;

const WS_ERROR_MESSAGE = "There was a problem processing your Document Review request. Please try again later.";

const UNHANDELLED_ERROR_MESSAGE = "Unhandelled Error fulfilling the request: "

const WS_URL = (token) =>
  buildWsUrl("review_doc", token);

const getAuthToken = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
};

const startKeepAlive = (ws) =>
  setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "ping" }));
    }
  }, WS_PING_INTERVAL_MS);

function handleParsedMessage(parsed, { setLoading }) {
  switch (parsed.type) {
    case "error":
      console.error("Processing error:", parsed.message);
      toast.error(`Error: ${parsed.message}`);
      setLoading(false);
      return true;

    case "info":
      console.info("Processing update:", parsed.message);
      toast.info(`Update: ${parsed.message}`);
      return true;

    case "pong":
      console.info("Received ping/pong reply:", parsed.message);
      return true;

    default:
      return false;
  }
}

const Reviewer = () => {
  const { setLoading } = useLoading();
  const [guidelineFile, setGuidelineFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [reportName, setReportName] = useState("");
  const [is_EU_MDR, set_EU_MDR] = useState(false);
  const [useCustomGuideline, setUseCustomGuideline] = useState(false);

  const handleCustomGuidelineChange = (e) => {
    setUseCustomGuideline(e.target.checked);
    if (!e.target.checked) {
      setGuidelineFile(null); // reset if unchecked
    }
  };

  const handleReportNameChange = (e) => {
    setReportName(e.target.value);
  };

  const handleChange = (e) => {
    set_EU_MDR(e.target.checked);
  };

  const handleReviewClick = async () => {
    if ((useCustomGuideline && !guidelineFile) || selectedFiles.length === 0 || !reportName.trim()) {
      toast.warning(
        "Please upload required documents and provide a report name before proceeding."
      );
      return;
    }

    const formData = new FormData();
    for (let i = 0; i < selectedFiles.length && i < 10; i++) {
      formData.append("user_doc_path", selectedFiles[i]);
    }
    formData.append("use_custom_guideline", useCustomGuideline);
    if (useCustomGuideline) {
      formData.append("guideline_doc_path", guidelineFile);
    }
    formData.append("reviewed_file_name", reportName);
    formData.append("llm_to_be_used", "gpt");
    formData.append("is_EU_MDR", is_EU_MDR ? "true" : "false");

    let guide_docPath;
    let user_docPath;
    try {
      setLoading(true, "Processing your file… This may take several minutes. Thank you for waiting.", "text-green-600");

      const response = await RegulifyService.docreviewer_upload_files(formData);
      if (response.status != 200) {
        throw new Error("Failed to upload document.. try again!");
      }
      guide_docPath = response.data.guide_doc_path
      user_docPath = response.data.user_doc_path
    } catch (error) {
      setLoading(false);
      console.error("Error during file upload processing:", error);
      toast.error("Error during file upload processing");
      return;
    }
    if (!user_docPath) return;

    let keepAliveInterval;

    try {
      const token = await getAuthToken();

      const ws = new WebSocket(WS_URL(token));
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        keepAliveInterval = startKeepAlive(ws);

        const payload = {
          guideline_file: useCustomGuideline ? guide_docPath : null,
          user_file: user_docPath,
          reviewed_file_name: reportName,
          llm_to_be_used: "gpt",
          is_EU_MDR: is_EU_MDR,
          use_custom_guideline: useCustomGuideline,
        };

        ws.send(JSON.stringify(payload));
        toast.info("Processing started...");
      };

      ws.onmessage = (event) => {
        const { data } = event;

        try {
          const parsed = JSON.parse(data);

          if (handleParsedMessage(parsed, { setLoading })) { return; }

          if (parsed.content && parsed.filename) {
            const byteCharacters = atob(parsed.content);
            const byteArray = new Uint8Array(
              [...byteCharacters].map((c) => c.charCodeAt(0)),
            );

            const blob = new Blob([byteArray], {
              type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = parsed.filename;
            link.click();

          } else {
            console.log("Unexpected output content received:", parsed);
          }
        } catch {
          console.log("Non-JSON message received:", data);
        }
      };

      ws.onerror = () => {
        console.error(WS_ERROR_MESSAGE);
        toast.error(WS_ERROR_MESSAGE);
        setLoading(false);
      };

      ws.onclose = () => {
        console.log("WebSocket closed");
        clearInterval(keepAliveInterval);
        setLoading(false);
      };
    } catch (error) {
      console.error(UNHANDELLED_ERROR_MESSAGE, error);
      setLoading(false);
    }
  };

  return (
    <div
      className="flex h-full w-full flex-col overflow-y-hidden"
      style={{ minHeight: "calc(100vh - 60px)" }}
    >
      <ContentTitle title={"Compliance Checker"} />
      <div className="card bg-base-100 mt-1 flex h-full w-full flex-col gap-4 overflow-y-auto p-4 shadow-xl">
        <p className="mt-2 text-sm text-gray-500">
          Default review standard: ISO 13485. Additional guidelines may be selected below.
        </p>
        {<div className="form-control mt-4 w-full">
          <label className="label">
            <span className="label-text font-semibold">
              Include EU MDR in the review?
            </span>
          </label>
          <input
            type="checkbox"
            name="is_eu_mdr_flag"
            className="checkbox ml-4"
            checked={is_EU_MDR}
            onChange={handleChange}
            required
          />
        </div>}
        {/* Custom Guideline Checkbox */}
        <div className="form-control mt-4 w-full">
          <label className="label cursor-pointer">
            <span className="label-text font-semibold">
              Include a custom guideline other than ISO 13485 or EU MDR?
            </span>
            <input
              type="checkbox"
              className="checkbox ml-4"
              checked={useCustomGuideline}
              onChange={handleCustomGuidelineChange}
            />
          </label>
        </div>
        {/* Guideline Upload (shown only if selected) */}
        {useCustomGuideline && (
          <div className="form-control mt-2 w-full max-w-md">
            <label className="label">
              <span className="label-text font-semibold">
                Select Custom Guideline Document
              </span>
            </label>
            <input
              type="file"
              accept=".pdf"
              id="guidelineFile"
              onChange={(e) => setGuidelineFile(e.target.files[0])}
              className="file-input file-input-bordered w-full"
            />
            <p className="mt-1 mb-1 text-sm text-gray-500">
              Accepted formats: <span className="font-medium">.pdf</span>
            </p>
            <a
              className="link mt-1 text-sm text-gray-400 hover:text-gray-500"
              download={"Guideline Software Development Plan.pdf"}
              href={guidelineDoc}
              target="_blank"
              rel="noreferrer"
            >
              Download Guideline Document sample
            </a>
          </div>
        )}

        <div className="form-control w-full max-w-md">
          <label className="label">
            <span className="label-text font-semibold">
              Select Multiple User Documents (Up to 10 docs)
            </span>
          </label>
          <input
            type="file"
            accept=".pdf"
            id="userFile"
            onChange={(e) => setSelectedFiles(e.target.files)}
            className="file-input file-input-bordered w-full"
            multiple
          />
          <p className="mt-1 mb-1 text-sm text-gray-500">
            Accepted formats:{" "}
            <span className="font-medium">.pdf</span>
          </p>
          <a
            className="link mt-1 text-sm text-gray-400 hover:text-gray-500"
            download={"DD_SDP_001 - Sample Device Software Rev.01.pdf"}
            href={userDoc}
            target="_blank"
            rel="noreferrer"
          >
            Download User Document sample
          </a>
        </div>
        <div className="form-control mt-4 w-full max-w-md">
          <label className="label">
            <span className="label-text font-semibold">Report Name:</span>
          </label>
          <input
            name="report_name"
            className="input input-bordered w-full"
            value={reportName}
            onChange={handleReportNameChange}
            required
          />
        </div>

        <button
          className="btn btn-primary w-fitmt-4 tooltip tooltip-right mt-4 w-fit rounded px-4 py-2"
          // className="btn btn-neutral w-fitmt-4 tooltip tooltip-right mt-4 w-fit rounded bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
          data-tip="Start the review process against global ISO 13485 standard along with any other options selected by user above"
          onClick={handleReviewClick}
        >
          Review Documents
        </button>
        <p className="mt-2 text-sm text-gray-500">
          Processing may take up to 30 minutes, depending on the number and content of uploaded documents.
          Check your Downloads folder for the review output file.
        </p>
      </div>
    </div>
  );
};
export default Reviewer;
